/**
 * Détail d'un dossier CEE ou MaPrimeAdapt' avec onglets dédiés.
 */
import { useEffect, useState, useCallback } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import api from '@/lib/api';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import {
  ArrowLeft, Save, ExternalLink, CheckCircle2, XCircle, FileDown, Send,
  Eye, Copy, FilePlus2, Phone, Mail, MapPin, Facebook, Instagram, FileText,
  ClipboardList, FolderOpen, History, LayoutGrid, Info, RefreshCw, AlertTriangle,
  Trash2, CopyPlus, ChevronLeft, ChevronRight, NotebookPen, ClipboardCheck,
} from 'lucide-react';
import ConfirmDialog from '@/components/ConfirmDialog';
import DossierNotesPanel from '@/components/DossierNotesPanel';
import { DocRequestPanel, DocRequestFiles } from '@/components/cee/DocRequestPanel';
import { toast } from 'sonner';

const STATUS_LABELS = {
  nouveau: 'Nouveau',
  prequalification: 'Préqualification',
  a_verifier: 'À vérifier',
  sans_reponse: 'Sans réponse',
  hors_criteres: 'Hors critères déclarés',
  offre_engagement: 'Offre & engagement',
  travaux: 'Travaux',
  pieces_a_completer: 'Pièces à compléter',
  controle_depot: 'Contrôle & dépôt',
  accepte: 'Accepté',
  refuse: 'Refusé',
  paye: 'Payé',
};

const QUESTIONNAIRE_LABELS = {
  not_sent: 'Non envoyé',
  sent: 'Envoyé',
  responded: 'Répondu',
};

const fmtDateTime = (iso) => iso ? new Date(iso).toLocaleString('fr-FR') : '—';
const fmtDate = (iso) => iso ? new Date(iso).toLocaleDateString('fr-FR') : '—';

const PlatformIcon = ({ platform, className }) => {
  if (!platform) return null;
  const p = platform.toLowerCase();
  if (p === 'facebook') return <Facebook className={className} />;
  if (p === 'instagram') return <Instagram className={className} />;
  return null;
};

/**
 * V3.1l-fix10 — Badge AMO (Assistant à Maîtrise d'Ouvrage) éditable.
 * V3.1q-fix5 — Remplacement champ libre par dropdown du registre AMO.
 *   - Vert : l'AMO couvre le CP du dossier (préfixe de zone)
 *   - Gris : ne couvre pas, mais sélectionnable
 *   - "— Non assigné —" : détache l'AMO
 */
const AmoBadge = ({ doc, onSave }) => {
  const [editing, setEditing] = useState(false);
  const [amos, setAmos] = useState([]);
  const [loadingAmos, setLoadingAmos] = useState(false);
  const [busy, setBusy] = useState(false);

  const dossierCp = (doc?.beneficiaire?.cp_ville || '').match(/\d{4,5}/)?.[0] || '';

  // Vérifie si un AMO couvre le CP du dossier
  const amoCoversCp = useCallback((amo) => {
    if (!dossierCp) return false;
    const zones = Array.isArray(amo?.zones) ? amo.zones : [];
    if (zones.length === 0) return false;
    return zones.some((z) => dossierCp.startsWith(String(z)));
  }, [dossierCp]);

  const loadAmos = async () => {
    if (amos.length > 0) return;
    setLoadingAmos(true);
    try {
      const r = await api.get('/cee/amos', { params: { active_only: true } });
      // V3.1q-fix7 — Endpoint renvoie { items, total }
      const list = Array.isArray(r.data)
        ? r.data
        : (r.data?.items || r.data?.amos || []);
      setAmos(list);
    } catch (_) { /* silent */ } finally { setLoadingAmos(false); }
  };

  const openEdit = () => { setEditing(true); loadAmos(); };

  const commit = async (amoId) => {
    setBusy(true);
    try {
      await onSave({ amo_id: amoId || '' });
      setEditing(false);
    } catch (_) { /* toast en amont */ } finally { setBusy(false); }
  };

  if (editing) {
    // Tri : AMO couvrants d'abord (vert), puis les autres (gris) alpha
    const sorted = [...amos].sort((a, b) => {
      const ca = amoCoversCp(a) ? 0 : 1;
      const cb = amoCoversCp(b) ? 0 : 1;
      if (ca !== cb) return ca - cb;
      return (a.nom || '').localeCompare(b.nom || '');
    });
    return (
      <div className="flex items-center gap-1" data-testid="amo-edit-row">
        <span className="text-[10px] px-1.5 py-0.5 rounded-l bg-amber-100 text-amber-800 font-bold border border-amber-200">
          AMO
        </span>
        <select
          autoFocus
          disabled={busy || loadingAmos}
          value={doc?.amo_id || ''}
          onChange={(e) => commit(e.target.value)}
          onBlur={() => setEditing(false)}
          className="h-7 text-sm w-64 border border-amber-200 rounded-r px-2 bg-white"
          data-testid="amo-select"
        >
          <option value="">— Non assigné —</option>
          {sorted.map((a) => {
            const covers = amoCoversCp(a);
            return (
              <option key={a.amo_id} value={a.amo_id}
                      style={{ color: covers ? '#15803d' : '#6b7280',
                                fontWeight: covers ? 600 : 400 }}>
                {covers ? '✓ ' : ''}{a.nom}
                {covers && dossierCp ? ` (couvre ${dossierCp})` : ''}
              </option>
            );
          })}
        </select>
      </div>
    );
  }

  // Affichage badge : vert si AMO assigné couvre le CP, orange si assigné mais ne couvre pas, gris si vide
  const currentAmo = amos.find((a) => a.amo_id === doc?.amo_id);
  const covers = currentAmo && amoCoversCp(currentAmo);
  const colorClasses = !doc?.amo_nom
    ? { border: 'border-amber-200', bg: 'bg-amber-50 hover:bg-amber-100',
        label: 'bg-amber-100 text-amber-800 border-amber-200',
        text: 'text-amber-500 italic' }
    : covers
    ? { border: 'border-emerald-300', bg: 'bg-emerald-50 hover:bg-emerald-100',
        label: 'bg-emerald-100 text-emerald-800 border-emerald-200',
        text: 'text-emerald-900 font-medium' }
    : { border: 'border-amber-200', bg: 'bg-amber-50 hover:bg-amber-100',
        label: 'bg-amber-100 text-amber-800 border-amber-200',
        text: 'text-amber-900 font-medium' };

  return (
    <button
      type="button"
      onClick={openEdit}
      className={`group inline-flex items-center gap-0 rounded border ${colorClasses.border} ${colorClasses.bg} transition-colors px-0 text-xs`}
      title={doc?.amo_nom
        ? (covers ? 'AMO couvrant le CP du dossier' : 'AMO assigné (ne couvre pas le CP)')
        : "Attribuer un AMO depuis le registre"}
      data-testid="amo-badge"
    >
      <span className={`px-1.5 py-0.5 rounded-l font-bold text-[10px] border-r ${colorClasses.label}`}>
        AMO
      </span>
      <span className={`px-2 py-0.5 ${colorClasses.text}`}>
        {doc?.amo_nom || 'à définir'}
      </span>
    </button>
  );
};

const AD_META_LABELS = {
  ad_id: 'ID publicité',
  ad_name: 'Nom de la publicité',
  adset_id: 'ID ensemble',
  adset_name: 'Nom de l\'ensemble',
  campaign_id: 'ID campagne',
  campaign_name: 'Nom de la campagne',
  is_organic: 'Lead organique',
  platform: 'Plateforme',
};

export default function CeeDossierDetail() {
  const { id } = useParams();
  const nav = useNavigate();
  const [doc, setDoc] = useState(null);
  const [loading, setLoading] = useState(true);
  const [beneficiaire, setBeneficiaire] = useState({});
  const [notes, setNotes] = useState('');
  const [pieces, setPieces] = useState([]);
  const [statuses, setStatuses] = useState([]);
  const [saving, setSaving] = useState(false);
  const [tab, setTab] = useState('overview');
  // V3.1l-fix6 — navigation prev/next + delete + duplicate
  const [neighbors, setNeighbors] = useState({ prev_id: null, next_id: null, position: null, total: null });
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [actionBusy, setActionBusy] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const [r, list, nb] = await Promise.all([
        api.get(`/cee/dossiers/${id}`),
        api.get('/cee/dossiers', { params: { status: 'prequalification' } }),
        api.get(`/cee/dossiers/${id}/neighbors`),
      ]);
      setDoc(r.data);
      setBeneficiaire(r.data.beneficiaire || {});
      setNotes(r.data.notes || '');
      setPieces(r.data.pieces || []);
      setStatuses(list.data.statuses || []);
      setNeighbors(nb.data || {});
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Erreur chargement');
    } finally { setLoading(false); }
  };

  // V3.1l-fix6 — raccourcis clavier flèches
  useEffect(() => {
    const handler = (e) => {
      if (['INPUT', 'TEXTAREA', 'SELECT'].includes(e.target.tagName)) return;
      if (e.key === 'ArrowLeft' && neighbors.prev_id) {
        nav(`/cee/dossiers/${neighbors.prev_id}`);
      } else if (e.key === 'ArrowRight' && neighbors.next_id) {
        nav(`/cee/dossiers/${neighbors.next_id}`);
      }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [neighbors, nav]);

  const handleDelete = async () => {
    setActionBusy(true);
    try {
      await api.delete(`/cee/dossiers/${id}`);
      toast.success('Dossier déplacé dans la corbeille');
      setConfirmDelete(false);
      nav('/cee/dossiers');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Erreur suppression');
    } finally { setActionBusy(false); }
  };

  const handleDuplicate = async () => {
    setActionBusy(true);
    try {
      const r = await api.post(`/cee/dossiers/${id}/duplicate`);
      toast.success(`Dossier dupliqué : ${r.data.new_dossier_id}`);
      nav(`/cee/dossiers/${r.data.new_dossier_id}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Erreur duplication');
    } finally { setActionBusy(false); }
  };

  const handleSaveAmo = async (payload) => {
    // V3.1q-fix5 — payload = { amo_id } (dropdown registre). Rétrocompat :
    // si on reçoit une string, on l'envoie en amo_nom (fallback historique).
    const body = typeof payload === 'string'
      ? { amo_nom: payload }
      : payload;
    try {
      const r = await api.put(`/cee/dossiers/${id}`, body);
      setDoc(r.data);
      const amoName = r.data?.amo_nom;
      toast.success(amoName
        ? `AMO enregistré : ${amoName}`
        : 'AMO retiré du dossier');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Erreur mise à jour AMO');
      throw e;
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [id]);

  const updateStatus = async (newStatus) => {
    setSaving(true);
    try {
      const r = await api.put(`/cee/dossiers/${id}`, { status: newStatus });
      setDoc(r.data);
      toast.success(`Statut : ${STATUS_LABELS[newStatus]}`);
    } catch (e) { toast.error(e?.response?.data?.detail || 'Erreur maj statut'); }
    finally { setSaving(false); }
  };

  const saveAll = async () => {
    setSaving(true);
    try {
      const r = await api.put(`/cee/dossiers/${id}`,
        { beneficiaire, notes, pieces });
      setDoc(r.data);
      toast.success('Dossier enregistré');
    } catch (e) { toast.error(e?.response?.data?.detail || 'Erreur enregistrement'); }
    finally { setSaving(false); }
  };

  const togglePiece = (idx) => {
    const next = [...pieces];
    next[idx] = { ...next[idx],
      statut: next[idx].statut === 'obtenue' ? 'manquante' : 'obtenue',
      uploaded_at: next[idx].statut === 'obtenue' ? null : new Date().toISOString() };
    setPieces(next);
  };

  const [aohBusy, setAohBusy] = useState(false);
  const [aohModalOpen, setAohModalOpen] = useState(false);
  const [aohPdfBlobUrl, setAohPdfBlobUrl] = useState(null);
  const [aohEmailOverride, setAohEmailOverride] = useState('');
  const [aohSendBusy, setAohSendBusy] = useState(false);

  // Cadre de Contribution — PDF obligatoire CEE
  const [cadreBusy, setCadreBusy] = useState(false);
  const [cadreModalOpen, setCadreModalOpen] = useState(false);
  const [cadrePdfBlobUrl, setCadrePdfBlobUrl] = useState(null);
  // Envoi lien signature bénéficiaire
  const [cadreEmailOverride, setCadreEmailOverride] = useState('');
  const [cadreSendBusy, setCadreSendBusy] = useState(false);
  // V3.1q-fix8 — Relance "Sans réponse" + prise de RDV téléphonique
  const [callbackBusy, setCallbackBusy] = useState(false);

  const openCadrePreview = async () => {
    setCadreBusy(true);
    try {
      const r = await api.get(`/cee/dossiers/${id}/cadre-contribution.pdf`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([r.data], { type: 'application/pdf' }));
      setCadrePdfBlobUrl(url);
      setCadreEmailOverride(doc?.beneficiaire?.email || '');
      setCadreModalOpen(true);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Erreur génération du cadre de contribution');
    } finally { setCadreBusy(false); }
  };
  const closeCadreModal = () => {
    setCadreModalOpen(false);
    if (cadrePdfBlobUrl) { window.URL.revokeObjectURL(cadrePdfBlobUrl); setCadrePdfBlobUrl(null); }
  };
  const downloadCadre = async () => {
    setCadreBusy(true);
    try {
      const r = await api.get(`/cee/dossiers/${id}/cadre-contribution.pdf`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([r.data], { type: 'application/pdf' }));
      const a = window.document.createElement('a');
      a.href = url;
      a.download = `CadreContribution_${doc.fiche_code}_${doc.dossier_id}.pdf`;
      window.document.body.appendChild(a);
      a.click(); a.remove();
      setTimeout(() => window.URL.revokeObjectURL(url), 1000);
      toast.success('Cadre de contribution téléchargé');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Erreur téléchargement');
    } finally { setCadreBusy(false); }
  };
  const sendCadreForSignature = async () => {
    setCadreSendBusy(true);
    try {
      const r = await api.post(`/cee/dossiers/${id}/cadre-contribution/send`, {
        email: cadreEmailOverride || undefined,
      });
      await load(); closeCadreModal();
      if (r.data.email_sent) {
        toast.success(`Lien de signature envoyé à ${cadreEmailOverride || doc.beneficiaire?.email || 'bénéficiaire'}`);
      } else {
        toast.warning('Lien créé mais envoi email échoué. Copiez le lien manuellement.');
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Erreur envoi du cadre de contribution');
    } finally { setCadreSendBusy(false); }
  };
  const copyCadreSignUrl = () => {
    const url = doc?.cadre_contribution?.token
      ? `${window.location.origin}/sign/cadre/${doc.cadre_contribution.token}` : null;
    if (!url) return;
    navigator.clipboard.writeText(url);
    toast.success('Lien copié');
  };

  const sendCallbackLink = async () => {
    const email = (doc?.beneficiaire?.email || '').trim();
    if (!email) {
      toast.error("Email du bénéficiaire manquant"); return;
    }
    setCallbackBusy(true);
    try {
      const r = await api.post(`/cee/dossiers/${id}/callback-booking/send`, {});
      await load();
      if (r.data.email_sent) {
        toast.success(`Email de relance envoyé à ${r.data.sent_to}`);
      } else {
        toast.warning('Lien créé mais email échoué.');
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Erreur envoi de la relance');
    } finally { setCallbackBusy(false); }
  };
  const copyCallbackUrl = () => {
    const t = doc?.callback_booking?.token;
    if (!t) return;
    navigator.clipboard.writeText(`${window.location.origin}/book/call/${t}`);
    toast.success('Lien copié');
  };

  const openAohPreview = async () => {
    setAohBusy(true);
    try {
      const r = await api.get(`/cee/dossiers/${id}/aoh.pdf`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([r.data], { type: 'application/pdf' }));
      setAohPdfBlobUrl(url);
      setAohEmailOverride(doc?.beneficiaire?.email || '');
      setAohModalOpen(true);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Erreur génération aperçu AoH');
    } finally { setAohBusy(false); }
  };
  const closeAohModal = () => {
    setAohModalOpen(false);
    if (aohPdfBlobUrl) { window.URL.revokeObjectURL(aohPdfBlobUrl); setAohPdfBlobUrl(null); }
  };
  const downloadAoh = async () => {
    setAohBusy(true);
    try {
      const r = await api.get(`/cee/dossiers/${id}/aoh.pdf`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([r.data], { type: 'application/pdf' }));
      const a = window.document.createElement('a');
      a.href = url;
      a.download = `AoH_${doc.fiche_code}_${doc.dossier_id}.pdf`;
      window.document.body.appendChild(a);
      a.click(); a.remove();
      setTimeout(() => window.URL.revokeObjectURL(url), 1000);
      toast.success('AoH téléchargée');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Erreur téléchargement AoH');
    } finally { setAohBusy(false); }
  };
  const sendAohForSignature = async () => {
    setAohSendBusy(true);
    try {
      const r = await api.post(`/cee/dossiers/${id}/aoh/send`, {
        email: aohEmailOverride || undefined,
      });
      await load(); closeAohModal();
      if (r.data.email_sent) {
        toast.success(`AoH envoyée à ${aohEmailOverride || doc.beneficiaire?.email || 'bénéficiaire'}`);
      } else {
        toast.warning('Lien créé mais envoi email échoué. Copiez le lien manuellement.');
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Erreur envoi AoH');
    } finally { setAohSendBusy(false); }
  };
  const copySignUrl = () => {
    const url = doc?.aoh?.token ? `${window.location.origin}/sign/aoh/${doc.aoh.token}` : null;
    if (!url) return;
    navigator.clipboard.writeText(url);
    toast.success('Lien copié');
  };

  const [devisBusy, setDevisBusy] = useState(false);
  const createDevis = async () => {
    setDevisBusy(true);
    try {
      const r = await api.post(`/cee/dossiers/${id}/devis`, {});
      toast.success(`Devis CEE créé : ${r.data.numero || r.data.devis_id}`);
      nav(`/devis/${r.data.devis_id}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Erreur création devis CEE');
    } finally { setDevisBusy(false); }
  };

  if (loading || !doc) return <div className="p-6 text-slate-500">Chargement…</div>;

  const isMpa = doc.aide_type === 'maprimeadapt';
  const title = isMpa
    ? `MaPrimeAdapt' — ${doc.beneficiaire?.nom_prenom || doc.beneficiaire?.nom || '(Prospect)'}`
    : `Dossier ${doc.fiche_code}`;
  const subtitle = isMpa ? doc.dossier_id : `v${doc.fiche_version} · ${doc.dossier_id}`;

  // Fix "Complet" : jamais complet si pieces_defined=false ou liste vide
  const piecesDefined = doc.pieces_defined === true;
  const missingCount = pieces.filter(
    (p) => p.obligatoire && p.statut !== 'obtenue' && p.statut !== 'non_applicable'
  ).length;
  const piecesStatusLabel = !piecesDefined
    ? 'Liste des pièces à définir — complétude non évaluée'
    : missingCount === 0 ? 'Complet' : `${missingCount} manquante(s)`;
  const piecesStatusColor = !piecesDefined
    ? 'bg-slate-100 text-slate-700'
    : missingCount === 0 ? 'bg-emerald-100 text-emerald-700' : 'bg-amber-100 text-amber-800';

  const qStatus = doc.questionnaire_status || 'not_sent';
  // V3.1q-fix7 — Normalise `questions` : les dossiers importés par email
  // le stockent en dict {label: value} ; les leads Meta Ads en liste
  // d'objets {key, label, value}. On convertit en liste homogène ici.
  const questionsRaw = doc.questions;
  const questions = Array.isArray(questionsRaw)
    ? questionsRaw
    : (questionsRaw && typeof questionsRaw === 'object')
      ? Object.entries(questionsRaw).map(([k, v]) => ({
          key: k, label: k, value: v,
        }))
      : [];
  const adMeta = (doc.ad_metadata && typeof doc.ad_metadata === 'object')
    ? doc.ad_metadata : {};

  const TABS = isMpa
    ? [
        { id: 'overview', label: 'Vue d\'ensemble', icon: LayoutGrid },
        { id: 'meta_form', label: 'Formulaire Meta', icon: FileText },
        { id: 'prequalif', label: 'Préqualification', icon: ClipboardList },
        { id: 'rapport', label: 'Rapport', icon: ClipboardCheck },
        { id: 'notes', label: 'Notes', icon: NotebookPen },
        { id: 'documents', label: 'Documents', icon: FolderOpen },
        { id: 'history', label: 'Historique', icon: History },
      ]
    : [
        { id: 'overview', label: 'Dossier', icon: LayoutGrid },
        { id: 'notes', label: 'Notes', icon: NotebookPen },
        { id: 'documents', label: 'Pièces', icon: FolderOpen },
        { id: 'history', label: 'Journal', icon: History },
      ];

  return (
    <div className="p-4 sm:p-6 max-w-6xl mx-auto space-y-4 sm:space-y-6" data-testid="cee-dossier-detail">
      {/* Breadcrumb + nav prev/next */}
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <div className="flex items-center gap-2 text-xs sm:text-sm text-slate-500 min-w-0">
          <Link to="/cee/dossiers" className="hover:text-[#1C49A6]">Dossiers</Link>
          <span>›</span>
          <span className="text-slate-900 font-medium font-mono truncate">{doc.dossier_id}</span>
        </div>
        <div className="flex items-center gap-1 shrink-0" data-testid="dossier-nav-arrows">
          <Button
            variant="outline" size="sm"
            disabled={!neighbors.prev_id}
            onClick={() => neighbors.prev_id && nav(`/cee/dossiers/${neighbors.prev_id}`)}
            data-testid="nav-prev-btn"
            title="Dossier précédent (←)"
          >
            <ChevronLeft className="w-4 h-4" />
          </Button>
          {neighbors.position && neighbors.total ? (
            <span className="text-[11px] text-slate-500 px-2 whitespace-nowrap"
                  data-testid="nav-position">
              {neighbors.position} / {neighbors.total}
            </span>
          ) : null}
          <Button
            variant="outline" size="sm"
            disabled={!neighbors.next_id}
            onClick={() => neighbors.next_id && nav(`/cee/dossiers/${neighbors.next_id}`)}
            data-testid="nav-next-btn"
            title="Dossier suivant (→)"
          >
            <ChevronRight className="w-4 h-4" />
          </Button>
        </div>
      </div>

      {/* Header */}
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            {isMpa && (
              <span className="text-[10px] px-1.5 py-0.5 rounded bg-indigo-100 text-indigo-700 font-semibold">
                MaPrimeAdapt'
              </span>
            )}
            {doc.source === 'meta_ads_instant_form' && (
              <span className="text-[10px] px-1.5 py-0.5 rounded bg-blue-50 text-blue-700 font-semibold flex items-center gap-1">
                <PlatformIcon platform={doc.source_platform} className="w-3 h-3" />
                Meta Ads{doc.source_platform ? ` · ${doc.source_platform}` : ''}
              </span>
            )}
          </div>
          <div className="flex items-start gap-2 flex-wrap mt-1">
            <h1 className="text-xl sm:text-2xl font-bold text-slate-900 break-words"
                data-testid="cee-dossier-title">
              {title}
            </h1>
            {isMpa && <AmoBadge doc={doc} onSave={handleSaveAmo} />}
          </div>
          <p className="text-xs sm:text-sm text-slate-500 mt-0.5 font-mono truncate">
            {subtitle}
          </p>
          {doc.user_email && (
            <p className="text-[11px] text-slate-500 mt-0.5" data-testid="dossier-creator">
              Créé par <span className="font-medium text-slate-700">{doc.user_email}</span>
              {doc.created_at && (
                <span className="text-slate-400"> · {fmtDateTime(doc.created_at)}</span>
              )}
            </p>
          )}
          {doc.source_dossier_id && (
            <p className="text-[11px] text-slate-400 mt-0.5">
              Duplication manuelle depuis{' '}
              <Link to={`/cee/dossiers/${doc.source_dossier_id}`}
                 className="text-[#1C49A6] hover:underline font-mono">
                {doc.source_dossier_id}
              </Link>
            </p>
          )}
          {/* V3.1l-fix6 — Actions Dupliquer / Supprimer */}
          <div className="flex gap-2 mt-3">
            <Button variant="outline" size="sm" onClick={handleDuplicate}
              disabled={actionBusy}
              data-testid="dossier-duplicate-btn">
              <CopyPlus className="w-3.5 h-3.5 mr-1.5" />
              Dupliquer
            </Button>
            <Button variant="outline" size="sm"
              onClick={() => setConfirmDelete(true)}
              disabled={actionBusy}
              className="border-red-200 text-red-600 hover:bg-red-50"
              data-testid="dossier-delete-btn">
              <Trash2 className="w-3.5 h-3.5 mr-1.5" />
              Supprimer
            </Button>
          </div>
        </div>
        {!isMpa && (
          <div className="text-right">
            <div className="text-xs text-slate-500">Prime CEE</div>
            <div className="text-2xl sm:text-3xl font-bold text-emerald-700 font-mono" data-testid="cee-dossier-prime">
              {doc.prime_eur_snapshot != null
                ? new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'EUR', maximumFractionDigits: 0 }).format(doc.prime_eur_snapshot)
                : <span className="text-amber-600 text-base">à vérifier</span>}
            </div>
            {doc.selected_delegataire?.delegataire_nom && (
              <div className="text-xs text-slate-600 mt-0.5">
                {doc.selected_delegataire.delegataire_nom}
                {doc.selected_tarif_type === 'precarite' && (
                  <span className="ml-1 text-orange-600 font-semibold">• précarité</span>
                )}
              </div>
            )}
          </div>
        )}
      </div>

      {/* 3 statuts séparés pour MPA : avancement commercial / préqualif / complétude */}
      {isMpa && (
        <div className="grid sm:grid-cols-3 gap-2 sm:gap-3" data-testid="mpa-status-row">
          <Card className="p-3 border-slate-200">
            <div className="text-[10px] uppercase font-semibold text-slate-500">Avancement commercial</div>
            <Badge className="mt-1 border-0 bg-slate-100 text-slate-800">
              {STATUS_LABELS[doc.status] || doc.status}
            </Badge>
          </Card>
          <Card className="p-3 border-slate-200">
            <div className="text-[10px] uppercase font-semibold text-slate-500">Préqualification</div>
            <Badge className={`mt-1 border-0 ${qStatus === 'responded' ? 'bg-emerald-100 text-emerald-700' : qStatus === 'sent' ? 'bg-blue-100 text-blue-700' : 'bg-slate-100 text-slate-600'}`}
                   data-testid="mpa-questionnaire-badge">
              {QUESTIONNAIRE_LABELS[qStatus]}
            </Badge>
          </Card>
          <Card className="p-3 border-slate-200">
            <div className="text-[10px] uppercase font-semibold text-slate-500">Complétude documentaire</div>
            <Badge className={`mt-1 border-0 ${piecesStatusColor}`}
                   data-testid="mpa-pieces-badge">
              {piecesStatusLabel}
            </Badge>
          </Card>
        </div>
      )}

      {/* Pipeline — statut commercial */}
      <Card className="p-3 sm:p-4 border-slate-200">
        <div className="text-xs font-semibold text-slate-600 uppercase mb-2">
          {isMpa ? 'Avancement commercial' : 'Pipeline'}
        </div>
        <div className="flex flex-wrap gap-1.5 sm:gap-2">
          {statuses.map((st) => (
            <button
              key={st}
              onClick={() => updateStatus(st)}
              disabled={saving || st === doc.status}
              data-testid={`cee-status-btn-${st}`}
              className={`px-2 sm:px-3 py-1 sm:py-1.5 rounded text-[11px] sm:text-xs font-medium transition
                ${st === doc.status
                  ? 'bg-[#1C49A6] text-white cursor-default'
                  : 'bg-slate-100 hover:bg-slate-200 text-slate-700'}`}
            >
              {STATUS_LABELS[st]}
            </button>
          ))}
        </div>
      </Card>

      {/* Onglets */}
      <div className="border-b border-slate-200 flex gap-1 overflow-x-auto -mx-4 sm:mx-0 px-4 sm:px-0"
           data-testid="dossier-tabs">
        {TABS.map((t) => {
          const Icon = t.icon;
          const active = tab === t.id;
          return (
            <button
              key={t.id}
              onClick={() => setTab(t.id)}
              data-testid={`tab-btn-${t.id}`}
              className={`px-3 py-2 text-xs sm:text-sm font-medium whitespace-nowrap border-b-2 transition flex items-center gap-1.5
                ${active ? 'border-[#1C49A6] text-[#1C49A6]' : 'border-transparent text-slate-500 hover:text-slate-900'}`}
            >
              <Icon className="w-3.5 h-3.5" />
              {t.label}
            </button>
          );
        })}
      </div>

      {/* ============ ONGLET VUE D'ENSEMBLE ============ */}
      {tab === 'overview' && (
        <div className="space-y-4">
          {/* Demandeur vs bénéficiaire (MPA uniquement) */}
          {isMpa && doc.demandeur && (
            <Card className="p-4 sm:p-6 border-slate-200 space-y-3" data-testid="mpa-demandeur-card">
              <div className="flex items-center justify-between flex-wrap gap-2">
                <h2 className="font-bold text-slate-900">Demandeur (formulaire Meta)</h2>
                <label className="inline-flex items-center gap-2 text-xs text-slate-600 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={doc.demandeur_est_beneficiaire !== false}
                    onChange={async (e) => {
                      try {
                        const r = await api.put(`/cee/dossiers/${id}`,
                          { demandeur_est_beneficiaire: e.target.checked });
                        setDoc(r.data);
                      } catch (err) {
                        toast.error('Impossible d\'enregistrer');
                      }
                    }}
                    data-testid="mpa-demandeur-is-benef-toggle"
                  />
                  Demandeur = bénéficiaire des travaux
                </label>
              </div>
              <div className="grid sm:grid-cols-3 gap-3 text-sm">
                <div>
                  <div className="text-[11px] text-slate-500">Nom</div>
                  <div className="text-slate-900">{doc.demandeur.nom_prenom || '—'}</div>
                </div>
                <div>
                  <div className="text-[11px] text-slate-500">Téléphone</div>
                  {doc.demandeur.telephone ? (
                    <a href={`tel:${doc.demandeur.telephone}`}
                       className="text-[#1C49A6] hover:underline">
                      {doc.demandeur.telephone}
                    </a>
                  ) : '—'}
                </div>
                <div className="min-w-0">
                  <div className="text-[11px] text-slate-500">Email</div>
                  <div className="text-slate-900 truncate">{doc.demandeur.email || '—'}</div>
                </div>
              </div>
              {doc.demandeur_est_beneficiaire === false && (
                <div className="text-[11px] text-slate-500 italic flex items-center gap-1">
                  <Info className="w-3 h-3" /> Le bénéficiaire des travaux est une personne différente — complétez ses coordonnées ci-dessous.
                </div>
              )}
            </Card>
          )}

          {/* Bénéficiaire */}
          <Card className="p-4 sm:p-6 border-slate-200 space-y-4">
            <div className="flex items-center justify-between flex-wrap gap-2">
              <h2 className="font-bold text-slate-900">
                {isMpa ? 'Bénéficiaire des travaux' : 'Bénéficiaire'}
              </h2>
              {doc.contact_id && (
                <Link to={`/contacts/${doc.contact_id}`} className="text-xs text-[#1C49A6] hover:underline">
                  Ouvrir contact {doc.contact_id}
                </Link>
              )}
            </div>
            <div className="grid sm:grid-cols-2 gap-3">
              <div>
                <label className="text-xs text-slate-500">Nom / Raison sociale</label>
                <Input value={beneficiaire.nom_prenom || beneficiaire.nom || ''}
                  onChange={(e) => setBeneficiaire({ ...beneficiaire, nom_prenom: e.target.value, nom: e.target.value })}
                  data-testid="cee-benef-nom" />
              </div>
              <div>
                <label className="text-xs text-slate-500">Email</label>
                <Input type="email" value={beneficiaire.email || ''}
                  onChange={(e) => setBeneficiaire({ ...beneficiaire, email: e.target.value })}
                  data-testid="cee-benef-email" />
              </div>
              <div>
                <label className="text-xs text-slate-500">Téléphone</label>
                <div className="flex gap-2">
                  <Input value={beneficiaire.telephone || ''}
                    onChange={(e) => setBeneficiaire({ ...beneficiaire, telephone: e.target.value })}
                    data-testid="cee-benef-tel" />
                  {beneficiaire.telephone && (
                    <a href={`tel:${beneficiaire.telephone}`}
                       className="inline-flex items-center justify-center w-10 h-10 rounded bg-emerald-50 text-emerald-700 hover:bg-emerald-100"
                       data-testid="cee-benef-tel-call">
                      <Phone className="w-4 h-4" />
                    </a>
                  )}
                </div>
              </div>
              <div>
                <label className="text-xs text-slate-500">Code postal</label>
                <Input value={beneficiaire.code_postal || ''}
                  onChange={(e) => setBeneficiaire({ ...beneficiaire, code_postal: e.target.value, cp_ville: e.target.value })}
                  data-testid="cee-benef-cp" />
              </div>
              {!isMpa && (
                <>
                  <div>
                    <label className="text-xs text-slate-500">Adresse chantier</label>
                    <Input value={beneficiaire.adresse || ''}
                      onChange={(e) => setBeneficiaire({ ...beneficiaire, adresse: e.target.value })}
                      data-testid="cee-benef-adresse" />
                  </div>
                  <div>
                    <label className="text-xs text-slate-500">SIRET (tertiaire)</label>
                    <Input value={beneficiaire.siret || ''}
                      onChange={(e) => setBeneficiaire({ ...beneficiaire, siret: e.target.value })}
                      data-testid="cee-benef-siret" />
                  </div>
                </>
              )}
            </div>
          </Card>

          {/* Infos clés MPA / Infos CEE */}
          {isMpa ? (
            <Card className="p-4 sm:p-6 border-slate-200">
              <div className="flex items-center justify-between flex-wrap gap-2 mb-3">
                <h2 className="font-bold text-slate-900">Informations de suivi</h2>
                <RefreshFromSourceButton dossierId={id} onDone={load} />
              </div>
              <div className="grid sm:grid-cols-2 gap-x-6 gap-y-2 text-sm">
                <div className="flex items-center gap-2 text-slate-700">
                  <MapPin className="w-4 h-4 text-slate-400" />
                  <span>Code postal : <b>{beneficiaire.code_postal || '—'}</b></span>
                </div>
                <div className="flex items-center gap-2 text-slate-700">
                  <ClipboardList className="w-4 h-4 text-slate-400" />
                  <span>Formulaire : <b>{doc.meta_form_name || '—'}</b></span>
                </div>
                <div className="flex items-center gap-2 text-slate-700">
                  <PlatformIcon platform={doc.source_platform} className="w-4 h-4 text-slate-400" />
                  <span>Source : <b>Meta Ads{doc.source_platform ? ` · ${doc.source_platform}` : ''}</b></span>
                </div>
                <div className="flex items-center gap-2 text-slate-700">
                  <History className="w-4 h-4 text-slate-400" />
                  <span>Reçu le : <b>{fmtDateTime(doc.imported_at)}</b></span>
                </div>
              </div>
            </Card>
          ) : null}

          <div className="flex flex-wrap justify-end gap-2">
            <Button
              onClick={saveAll}
              disabled={saving}
              className="bg-[#1C49A6] hover:bg-[#173B85]"
              data-testid="cee-dossier-save"
            >
              <Save className="w-4 h-4 mr-2" /> Enregistrer
            </Button>
          </div>
        </div>
      )}

      {/* ============ ONGLET FORMULAIRE META ============ */}
      {tab === 'meta_form' && (
        <div className="space-y-4">
          <Card className="p-4 sm:p-6 border-slate-200" data-testid="meta-questions-card">
            <h2 className="font-bold text-slate-900 mb-1">Réponses au formulaire Meta</h2>
            <p className="text-xs text-slate-500 mb-4">
              Questions posées au prospect dans le formulaire instantané Facebook / Instagram.
            </p>
            {questions.length === 0 ? (
              <p className="text-sm text-slate-500 italic">Aucune réponse disponible.</p>
            ) : (
              <div className="space-y-4">
                {questions.map((q, i) => (
                  <div key={q.key || i} className="pb-3 border-b border-slate-100 last:border-b-0"
                       data-testid={`meta-question-${i}`}>
                    <div className="text-xs text-slate-500 mb-1 leading-snug">{q.label}</div>
                    <div className="text-sm text-slate-900 font-medium break-words">
                      {q.value || <span className="text-slate-400 italic">(sans réponse)</span>}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </Card>

          {/* Informations de provenance (ad metadata) */}
          {Object.keys(adMeta).length > 0 && (
            <Card className="p-4 sm:p-6 border-slate-200 bg-slate-50/50"
                  data-testid="meta-ad-provenance-card">
              <h3 className="font-semibold text-slate-700 text-sm mb-3 flex items-center gap-2">
                <Info className="w-4 h-4" />
                Informations de provenance publicitaire
              </h3>
              <div className="grid sm:grid-cols-2 gap-x-4 gap-y-2 text-xs">
                {Object.entries(adMeta).map(([k, v]) => (
                  <div key={k} className="flex gap-2 min-w-0">
                    <span className="text-slate-500 shrink-0">{AD_META_LABELS[k] || k} :</span>
                    <span className="text-slate-900 font-mono break-all">{String(v)}</span>
                  </div>
                ))}
              </div>
            </Card>
          )}

          {/* Debug — raw payload (fermé par défaut) */}
          {doc.raw_answers && Object.keys(doc.raw_answers).length > 0 && (
            <details className="text-xs" data-testid="meta-raw-details">
              <summary className="cursor-pointer text-slate-500 hover:text-slate-900">
                Afficher les données brutes (debug)
              </summary>
              <pre className="mt-2 bg-slate-900 text-slate-100 rounded p-3 overflow-auto text-[10px]">
                {JSON.stringify(doc.raw_answers, null, 2)}
              </pre>
            </details>
          )}
        </div>
      )}

      {/* ============ ONGLET PRÉQUALIFICATION ============ */}
      {tab === 'prequalif' && (
        <PrequalifTab doc={doc} id={id} onReload={load} />
      )}

      {/* ============ ONGLET RAPPORT (MaPrimeAdapt' uniquement) ============ */}
      {tab === 'rapport' && isMpa && (
        <RapportTab doc={doc} id={id} onReload={load} />
      )}

      {/* ============ ONGLET DOCUMENTS ============ */}
      {tab === 'documents' && (
        <Card className="p-4 sm:p-6 border-slate-200 space-y-3" data-testid="documents-card">
          <div className="flex items-center justify-between flex-wrap gap-2">
            <h2 className="font-bold text-slate-900">Pièces du dossier</h2>
            <Badge className={`${piecesStatusColor} border-0 text-xs`}>
              {piecesStatusLabel}
            </Badge>
          </div>

          {!piecesDefined ? (
            <div className="p-6 bg-slate-50 rounded-lg text-center space-y-2">
              <FolderOpen className="w-10 h-10 mx-auto text-slate-300" />
              <p className="text-sm text-slate-700 font-medium">
                Liste des pièces à définir
              </p>
              <p className="text-xs text-slate-500 max-w-md mx-auto">
                Aucune checklist de documents n'est encore associée à ce dossier.
                La complétude ne peut pas être évaluée tant que vous n'avez pas défini
                les pièces attendues pour MaPrimeAdapt'.
              </p>
            </div>
          ) : pieces.length === 0 ? (
            <p className="text-sm text-slate-500 italic">Aucune pièce dans la liste.</p>
          ) : (
            <div className="space-y-2">
              {pieces.map((p, i) => (
                <div key={p.code} className="flex items-center gap-3 p-2 border border-slate-100 rounded">
                  <button onClick={() => togglePiece(i)}
                    data-testid={`cee-piece-toggle-${p.code}`}>
                    {p.statut === 'obtenue'
                      ? <CheckCircle2 className="w-5 h-5 text-emerald-600" />
                      : <XCircle className="w-5 h-5 text-slate-300" />}
                  </button>
                  <div className="flex-1 min-w-0">
                    <div className="font-medium text-slate-900 text-sm">{p.label}</div>
                    <div className="text-xs text-slate-500">
                      {p.code}{p.obligatoire ? ' · obligatoire' : ' · facultative'}
                      {p.uploaded_at ? ` · marquée obtenue le ${fmtDate(p.uploaded_at)}` : ''}
                    </div>
                  </div>
                  <select
                    value={p.statut}
                    onChange={(e) => {
                      const next = [...pieces];
                      next[i] = { ...next[i], statut: e.target.value };
                      setPieces(next);
                    }}
                    className="text-xs border border-slate-200 rounded px-2 py-1"
                  >
                    <option value="manquante">Manquante</option>
                    <option value="obtenue">Obtenue</option>
                    <option value="non_applicable">Non applicable</option>
                  </select>
                </div>
              ))}
            </div>
          )}

          {isMpa && <DocRequestFiles id={id} />}

          {/* Notes rattachées aux documents */}
          <div className="pt-4 border-t border-slate-100">
            <h3 className="font-semibold text-slate-900 text-sm mb-2">Notes internes</h3>
            <Textarea value={notes} onChange={(e) => setNotes(e.target.value)}
              rows={3} placeholder="Notes internes sur ce dossier…"
              data-testid="cee-dossier-notes" />
            <Button onClick={saveAll} disabled={saving}
              className="mt-2 bg-[#1C49A6] hover:bg-[#173B85]" size="sm">
              <Save className="w-4 h-4 mr-2" /> Enregistrer
            </Button>
          </div>
        </Card>
      )}

      {/* ============ ONGLET NOTES & RAPPORTS ============ */}
      {tab === 'notes' && (
        <DossierNotesPanel dossierId={id} amo={{
          amo_id: doc.amo_id,
          amo_nom: doc.amo_nom,
          amo_email: doc.amo_email,
        }} />
      )}

      {/* ============ ONGLET HISTORIQUE ============ */}
      {tab === 'history' && (
        <Card className="p-4 sm:p-6 border-slate-200" data-testid="history-card">
          <h2 className="font-bold text-slate-900 mb-3">Historique du dossier</h2>
          {doc.timeline?.length > 0 ? (
            <ul className="space-y-2" data-testid="dossier-history-list">
              {[...doc.timeline].reverse().map((t, i) => (
                <li key={i} className="flex gap-3 text-xs sm:text-sm border-b border-slate-100 pb-2 last:border-0">
                  <div className="text-slate-400 font-mono shrink-0 w-32 sm:w-40">
                    {fmtDateTime(t.at)}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="text-slate-900">{t.action}</div>
                    {t.status_change && (
                      <div className="inline-block text-[10px] px-1.5 py-0.5 rounded bg-blue-50 text-blue-700 border border-blue-200 mt-1">
                        {t.status_change}
                      </div>
                    )}
                    {(t.user_name || t.user) && (
                      <div className="text-[10px] text-slate-500 mt-1">
                        par <span className="font-medium">{t.user_name || t.user}</span>
                        {t.user_name && t.user && t.user_name !== t.user && (
                          <span className="text-slate-400"> &lt;{t.user}&gt;</span>
                        )}
                      </div>
                    )}
                  </div>
                </li>
              ))}
            </ul>
          ) : (
            <p className="text-sm text-slate-500 italic">Aucune action enregistrée.</p>
          )}
        </Card>
      )}

      {/* Actions bas de page (CEE uniquement) */}
      {!isMpa && tab === 'overview' && (
        <div className="flex flex-wrap justify-between gap-2">
          <Button variant="ghost" onClick={() => nav('/cee/dossiers')}>
            <ArrowLeft className="w-4 h-4 mr-2" /> Retour
          </Button>
          <div className="flex flex-wrap gap-2">
            {doc.linked_devis_id ? (
              <Button
                variant="outline"
                className="border-emerald-600 text-emerald-700 hover:bg-emerald-50"
                onClick={() => nav(`/devis/${doc.linked_devis_id}`)}
                data-testid="cee-dossier-open-devis"
              >
                <FilePlus2 className="w-4 h-4 mr-2" /> Ouvrir le devis CEE
              </Button>
            ) : (
              <Button
                onClick={createDevis}
                disabled={devisBusy}
                variant="outline"
                className="border-emerald-600 text-emerald-700 hover:bg-emerald-50"
                data-testid="cee-dossier-create-devis"
              >
                <FilePlus2 className="w-4 h-4 mr-2" />
                {devisBusy ? 'Création…' : 'Créer devis CEE'}
              </Button>
            )}
            <Button
              onClick={openAohPreview}
              disabled={aohBusy}
              variant="outline"
              className="border-[#1C49A6] text-[#1C49A6] hover:bg-blue-50"
              data-testid="cee-dossier-aoh-btn"
            >
              <Eye className="w-4 h-4 mr-2" />
              {aohBusy ? 'Chargement…' : 'AoH : aperçu & envoi signature'}
            </Button>
            <Button
              onClick={openCadrePreview}
              disabled={cadreBusy}
              variant="outline"
              className="border-amber-500 text-amber-700 hover:bg-amber-50"
              data-testid="cee-dossier-cadre-btn"
              title="Cadre de Contribution — document obligatoire CEE (arrêté du 22/12/2017)"
            >
              <FileText className="w-4 h-4 mr-2" />
              {cadreBusy ? 'Chargement…' : 'Cadre de contribution'}
            </Button>
          </div>
        </div>
      )}

      {/* Statut signature AoH (CEE uniquement, hors onglets) */}
      {!isMpa && doc.aoh?.token && tab === 'overview' && (
        <Card className="p-4 border-slate-200" data-testid="cee-aoh-status-card">
          <div className="flex items-start justify-between gap-3 flex-wrap">
            <div className="flex-1 min-w-0">
              <div className="text-xs font-semibold text-slate-600 uppercase mb-1">
                Signature électronique AoH
              </div>
              {doc.aoh.status === 'signed' ? (
                <div className="space-y-1">
                  <div className="flex items-center gap-2">
                    <CheckCircle2 className="w-5 h-5 text-emerald-600" />
                    <span className="font-semibold text-emerald-800">
                      Signée par {doc.aoh.signed_by_name}
                      {doc.aoh.signed_by_role && ` (${doc.aoh.signed_by_role})`}
                    </span>
                  </div>
                  <div className="text-xs text-slate-500 ml-7">
                    Le {fmtDateTime(doc.aoh.signed_at)}
                    {doc.aoh.signed_ip && ` · IP ${doc.aoh.signed_ip}`}
                  </div>
                </div>
              ) : (
                <div className="space-y-1">
                  <div className="text-sm text-slate-900">
                    En attente de signature — envoyée à <b>{doc.aoh.sent_to}</b>
                  </div>
                  <div className="text-xs text-slate-500">
                    Le {fmtDateTime(doc.aoh.sent_at)}
                    {doc.aoh.expires_at && (
                      <> · expire le {fmtDate(doc.aoh.expires_at)}</>
                    )}
                  </div>
                </div>
              )}
            </div>
            <div className="flex flex-col gap-2 shrink-0">
              {doc.aoh.signed_pdf_path && (
                <a
                  href={`${process.env.REACT_APP_BACKEND_URL}/api/storage/${doc.aoh.signed_pdf_path}`}
                  target="_blank" rel="noopener noreferrer"
                  className="inline-flex items-center gap-1.5 text-xs font-medium text-[#1C49A6] hover:underline"
                >
                  <FileDown className="w-3.5 h-3.5" /> PDF signé
                </a>
              )}
              {doc.aoh.status !== 'signed' && (
                <button
                  onClick={copySignUrl}
                  className="inline-flex items-center gap-1.5 text-xs font-medium text-slate-600 hover:text-[#1C49A6]"
                >
                  <Copy className="w-3.5 h-3.5" /> Copier lien
                </button>
              )}
            </div>
          </div>
        </Card>
      )}

      {/* Statut signature Cadre de contribution (CEE uniquement, hors onglets) */}
      {!isMpa && doc.cadre_contribution?.token && tab === 'overview' && (
        <Card className="p-4 border-slate-200" data-testid="cee-cadre-status-card">
          <div className="flex items-start justify-between gap-3 flex-wrap">
            <div className="flex-1 min-w-0">
              <div className="text-xs font-semibold text-amber-700 uppercase mb-1">
                Signature électronique — Cadre de contribution
              </div>
              {doc.cadre_contribution.status === 'signed' ? (
                <div className="space-y-1">
                  <div className="flex items-center gap-2">
                    <CheckCircle2 className="w-5 h-5 text-emerald-600" />
                    <span className="font-semibold text-emerald-800">
                      Signé par {doc.cadre_contribution.signed_by_name}
                      {doc.cadre_contribution.signed_by_role && ` (${doc.cadre_contribution.signed_by_role})`}
                    </span>
                  </div>
                  <div className="text-xs text-slate-500 ml-7">
                    Le {fmtDateTime(doc.cadre_contribution.signed_at)}
                    {doc.cadre_contribution.signed_ip && ` · IP ${doc.cadre_contribution.signed_ip}`}
                  </div>
                </div>
              ) : (
                <div className="space-y-1">
                  <div className="text-sm text-slate-900">
                    En attente de signature — envoyé à <b>{doc.cadre_contribution.sent_to}</b>
                  </div>
                  <div className="text-xs text-slate-500">
                    Le {fmtDateTime(doc.cadre_contribution.sent_at)}
                    {doc.cadre_contribution.expires_at && (
                      <> · expire le {fmtDate(doc.cadre_contribution.expires_at)}</>
                    )}
                  </div>
                </div>
              )}
            </div>
            <div className="flex flex-col gap-2 shrink-0">
              {doc.cadre_contribution.signed_pdf_path && (
                <a
                  href={`${process.env.REACT_APP_BACKEND_URL}/api/storage/${doc.cadre_contribution.signed_pdf_path}`}
                  target="_blank" rel="noopener noreferrer"
                  className="inline-flex items-center gap-1.5 text-xs font-medium text-amber-700 hover:underline"
                >
                  <FileDown className="w-3.5 h-3.5" /> PDF signé
                </a>
              )}
              {doc.cadre_contribution.status !== 'signed' && (
                <button
                  onClick={copyCadreSignUrl}
                  className="inline-flex items-center gap-1.5 text-xs font-medium text-slate-600 hover:text-amber-700"
                >
                  <Copy className="w-3.5 h-3.5" /> Copier lien
                </button>
              )}
            </div>
          </div>
        </Card>
      )}

      {/* V3.1q-fix8 — Relance "Sans réponse" / RDV téléphonique */}
      {tab === 'overview' && (doc.status === 'sans_reponse' || doc.callback_booking?.token) && (
        <Card className="p-4 border-slate-200" data-testid="callback-booking-card">
          <div className="flex items-start justify-between gap-3 flex-wrap">
            <div className="flex-1 min-w-0">
              <div className="text-xs font-semibold text-blue-700 uppercase mb-1">
                Relance téléphonique
              </div>
              {!doc.callback_booking?.token ? (
                <div className="space-y-1">
                  <div className="text-sm text-slate-900">
                    Aucune relance envoyée. Envoyez un lien de prise de RDV.
                  </div>
                  <div className="text-xs text-slate-500">
                    L'email contiendra un lien vers un calendrier (créneaux 15 min, 10 jours ouvrés + samedi).
                  </div>
                </div>
              ) : doc.callback_booking.status === 'booked' ? (
                <div className="space-y-1">
                  <div className="flex items-center gap-2">
                    <CheckCircle2 className="w-5 h-5 text-emerald-600" />
                    <span className="font-semibold text-emerald-800">
                      RDV réservé le {fmtDateTime(doc.callback_booking.booked_slot_start)}
                    </span>
                  </div>
                  <div className="text-xs text-slate-500 ml-7">
                    Réservé le {fmtDateTime(doc.callback_booking.booked_at)}
                    {doc.callback_booking.booked_phone && ` · ${doc.callback_booking.booked_phone}`}
                    {doc.callback_booking.agenda_event_id && ` · Agenda ${doc.callback_booking.agenda_event_id}`}
                  </div>
                </div>
              ) : (
                <div className="space-y-1">
                  <div className="text-sm text-slate-900">
                    En attente de réservation — envoyé à <b>{doc.callback_booking.sent_to}</b>
                  </div>
                  <div className="text-xs text-slate-500">
                    Le {fmtDateTime(doc.callback_booking.sent_at)}
                    {doc.callback_booking.expires_at && (
                      <> · expire le {fmtDate(doc.callback_booking.expires_at)}</>
                    )}
                  </div>
                </div>
              )}
            </div>
            <div className="flex flex-col items-end gap-2 shrink-0">
              {doc.callback_booking?.status !== 'booked' && (
                <Button
                  onClick={sendCallbackLink}
                  disabled={callbackBusy}
                  className="bg-[#004AAD] hover:bg-[#003580]"
                  data-testid="callback-send-btn"
                >
                  <Send className="w-4 h-4 mr-2" />
                  {callbackBusy ? 'Envoi…' : (doc.callback_booking?.token ? 'Renvoyer le lien' : 'Envoyer email de relance')}
                </Button>
              )}
              {doc.callback_booking?.token && doc.callback_booking?.status !== 'booked' && (
                <button
                  onClick={copyCallbackUrl}
                  className="inline-flex items-center gap-1.5 text-xs font-medium text-slate-600 hover:text-blue-700"
                >
                  <Copy className="w-3.5 h-3.5" /> Copier lien RDV
                </button>
              )}
            </div>
          </div>
        </Card>
      )}

      {/* Modal AoH preview */}
      {aohModalOpen && (
        <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4"
             onClick={closeAohModal}>
          <div className="bg-white rounded-xl w-full max-w-5xl max-h-[90vh] overflow-hidden flex flex-col"
               onClick={(e) => e.stopPropagation()}>
            <div className="p-4 border-b border-slate-200 flex items-center justify-between">
              <div>
                <h2 className="font-bold text-slate-900">Aperçu AoH avant envoi</h2>
                <p className="text-xs text-slate-500 mt-0.5">
                  Vérifiez le document — il sera envoyé par email pour signature
                </p>
              </div>
              <button onClick={closeAohModal} className="text-slate-400 hover:text-slate-900 text-2xl leading-none px-2">×</button>
            </div>
            <div className="flex-1 overflow-hidden bg-slate-100">
              {aohPdfBlobUrl && (
                <iframe src={aohPdfBlobUrl} title="Aperçu AoH" className="w-full h-full" />
              )}
            </div>
            <div className="p-4 border-t border-slate-200 bg-slate-50">
              <div className="flex flex-wrap items-end gap-3">
                <div className="flex-1 min-w-[240px]">
                  <label className="text-xs text-slate-500 block mb-1">
                    Email du bénéficiaire (destinataire de la signature)
                  </label>
                  <Input type="email" value={aohEmailOverride}
                    onChange={(e) => setAohEmailOverride(e.target.value)}
                    placeholder="beneficiaire@exemple.fr" />
                </div>
                <Button variant="outline" onClick={downloadAoh} disabled={aohBusy}>
                  <FileDown className="w-4 h-4 mr-2" /> Télécharger
                </Button>
                <Button onClick={sendAohForSignature} disabled={aohSendBusy || !aohEmailOverride}
                  className="bg-[#1C49A6] hover:bg-[#173B85]">
                  <Send className="w-4 h-4 mr-2" />
                  {aohSendBusy ? 'Envoi…' : 'Envoyer pour signature'}
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Modal Cadre de Contribution preview */}
      {cadreModalOpen && (
        <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4"
             onClick={closeCadreModal} data-testid="cadre-modal">
          <div className="bg-white rounded-xl w-full max-w-5xl max-h-[90vh] overflow-hidden flex flex-col"
               onClick={(e) => e.stopPropagation()}>
            <div className="p-4 border-b border-slate-200 flex items-center justify-between">
              <div>
                <h2 className="font-bold text-slate-900">Cadre de Contribution CEE</h2>
                <p className="text-xs text-slate-500 mt-0.5">
                  Document obligatoire — arrêté du 22 décembre 2017 modifié · à faire signer avant engagement des travaux
                </p>
              </div>
              <button onClick={closeCadreModal} className="text-slate-400 hover:text-slate-900 text-2xl leading-none px-2">×</button>
            </div>
            <div className="flex-1 overflow-hidden bg-slate-100">
              {cadrePdfBlobUrl && (
                <iframe src={cadrePdfBlobUrl} title="Aperçu Cadre de Contribution" className="w-full h-full" data-testid="cadre-iframe" />
              )}
            </div>
            <div className="p-4 border-t border-slate-200 bg-slate-50">
              <div className="flex flex-wrap items-end gap-3">
                <div className="flex-1 min-w-[240px]">
                  <label className="text-xs text-slate-500 block mb-1">
                    Email du bénéficiaire (destinataire du lien de signature)
                  </label>
                  <Input
                    type="email"
                    value={cadreEmailOverride}
                    onChange={(e) => setCadreEmailOverride(e.target.value)}
                    placeholder="beneficiaire@exemple.fr"
                    data-testid="cadre-email-input"
                  />
                </div>
                <Button variant="outline" onClick={downloadCadre} disabled={cadreBusy} data-testid="cadre-download-btn">
                  <FileDown className="w-4 h-4 mr-2" />
                  {cadreBusy ? 'Génération…' : 'Télécharger'}
                </Button>
                <Button
                  onClick={sendCadreForSignature}
                  disabled={cadreSendBusy || !cadreEmailOverride}
                  className="bg-amber-600 hover:bg-amber-700"
                  data-testid="cadre-send-signature-btn"
                >
                  <Send className="w-4 h-4 mr-2" />
                  {cadreSendBusy ? 'Envoi…' : 'Envoyer pour signature'}
                </Button>
              </div>
              <p className="text-xs text-slate-500 mt-2">
                Un email avec le PDF en pièce jointe et un lien de signature (valable 7 jours) sera envoyé au bénéficiaire.
              </p>
            </div>
          </div>
        </div>
      )}

      {/* V3.1l-fix6 — Modal confirmation suppression */}
      <ConfirmDialog
        open={confirmDelete}
        danger
        title="Déplacer ce dossier dans la corbeille ?"
        description={
          `${doc.beneficiaire?.nom_prenom || doc.beneficiaire?.nom || '(sans nom)'}\n` +
          `Réf : ${doc.dossier_id}\n\n` +
          `• Le dossier sera masqué de la liste principale (restaurable depuis la corbeille).\n` +
          `• Les relances / tokens de questionnaire associés sont invalidés.\n` +
          `• Un lead Meta supprimé ne sera PAS recréé à la prochaine synchronisation.\n` +
          `• Le contact partagé et la ligne Google Sheets d'origine restent intacts.`
        }
        confirmLabel="Déplacer dans la corbeille"
        busy={actionBusy}
        onCancel={() => setConfirmDelete(false)}
        onConfirm={handleDelete}
      />
    </div>
  );
}


// ============================================================================
// Composants auxiliaires MaPrimeAdapt'
// ============================================================================

function RefreshFromSourceButton({ dossierId, onDone }) {
  const [busy, setBusy] = useState(false);
  const handle = async () => {
    if (busy) return;
    setBusy(true);
    try {
      const r = await api.post(`/meta-sync/dossiers/${dossierId}/refresh-from-source`);
      const n = r.data.changes_count || 0;
      if (n > 0) {
        toast.success(`${n} champ(s) mis à jour depuis Google Sheets`);
      } else {
        toast.info('Aucune évolution depuis la source');
      }
      onDone && onDone();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Erreur rafraîchissement');
    } finally { setBusy(false); }
  };
  return (
    <Button
      variant="outline" size="sm" onClick={handle} disabled={busy}
      className="text-xs border-slate-200"
      data-testid="refresh-from-source-btn"
    >
      <RefreshCw className={`w-3.5 h-3.5 mr-1.5 ${busy ? 'animate-spin' : ''}`} />
      {busy ? 'Rafraîchissement…' : 'Rafraîchir depuis la source'}
    </Button>
  );
}

/**
 * V3.1q-fix5 — Onglet Rapport (MaPrimeAdapt' uniquement).
 * Permet de saisir : éligibilité (cases à cocher), RFR, nombre de parts,
 * compte Anah, identité numérique, âge, documents en possession. Et de
 * générer / télécharger / envoyer le PDF rapport à l'AMO rattaché.
 */
const ELIGIBILITY_OPTIONS = [
  { key: 'age_70_plus', label: "Âge ≥ 70 ans (sans condition de perte d'autonomie)" },
  { key: 'age_60_69_gir', label: "60-69 ans en perte d'autonomie précoce (GIR 1 à 6)" },
  { key: 'handicap_pch', label: "Handicap ≥ 50 % ou éligible PCH (sans condition d'âge)" },
];
const DOCUMENT_OPTIONS = [
  { key: 'cni', label: "Carte Nationale d'Identité (CNI)" },
  { key: 'avis_impots', label: "Avis d'imposition" },
  { key: 'taxe_fonciere', label: "Taxe foncière" },
  { key: 'carte_invalidite', label: "Carte d'invalidité (si handicap)" },
];

function RapportTab({ doc, id, onReload }) {
  const [form, setForm] = useState(() => ({
    age: doc?.rapport?.age ?? '',
    revenu_fiscal_reference: doc?.rapport?.revenu_fiscal_reference ?? '',
    nombre_parts: doc?.rapport?.nombre_parts ?? '',
    menage_personnes: doc?.rapport?.menage_personnes ?? '',
    adresse_complete: doc?.rapport?.adresse_complete
      ?? [doc?.beneficiaire?.adresse, doc?.beneficiaire?.cp_ville]
          .filter(Boolean).join(' — '),
    compte_anah: doc?.rapport?.compte_anah ?? null,
    identite_numerique: doc?.rapport?.identite_numerique ?? null,
    eligibilite: { ...(doc?.rapport?.eligibilite || {}) },
    documents: { ...(doc?.rapport?.documents || {}) },
    notes: doc?.rapport?.notes ?? '',
  }));
  const [bareme, setBareme] = useState(null);
  const [saving, setSaving] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [showSendModal, setShowSendModal] = useState(false);
  const [sendMode, setSendMode] = useState('amo'); // 'amo' | 'custom'
  const [customEmail, setCustomEmail] = useState('');
  const [customMsg, setCustomMsg] = useState('');
  const [sending, setSending] = useState(false);

  const dossierCp = (doc?.beneficiaire?.cp_ville || '').match(/\d{5}/)?.[0] || '';

  // Live-compute du barème Anah à chaque changement de personnes / RFR / CP
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const r = await api.get('/cee/anah-bareme', {
          params: {
            cp: dossierCp || undefined,
            personnes: form.menage_personnes || undefined,
            rfr: form.revenu_fiscal_reference || undefined,
          },
        });
        if (!cancelled) setBareme(r.data);
      } catch { if (!cancelled) setBareme(null); }
    })();
    return () => { cancelled = true; };
  }, [dossierCp, form.menage_personnes, form.revenu_fiscal_reference]);

  const toggleElig = (k) => setForm((f) => ({
    ...f, eligibilite: { ...f.eligibilite, [k]: !f.eligibilite[k] },
  }));
  const toggleDoc = (k) => setForm((f) => ({
    ...f, documents: { ...f.documents, [k]: !f.documents[k] },
  }));
  const setYN = (field, value) => setForm((f) => ({ ...f, [field]: value }));

  const save = async () => {
    setSaving(true);
    try {
      const payload = {
        rapport: {
          ...form,
          age: form.age === '' ? null : Number(form.age),
          revenu_fiscal_reference: form.revenu_fiscal_reference === ''
            ? null : Number(form.revenu_fiscal_reference),
          nombre_parts: form.nombre_parts === ''
            ? null : Number(form.nombre_parts),
          menage_personnes: form.menage_personnes === ''
            ? null : Number(form.menage_personnes),
        },
      };
      await api.put(`/cee/dossiers/${id}`, payload);
      toast.success('Rapport enregistré');
      onReload && onReload();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Erreur enregistrement');
    } finally { setSaving(false); }
  };

  const downloadPdf = async () => {
    setDownloading(true);
    try {
      const r = await api.get(`/cee/dossiers/${id}/rapport.pdf`, {
        responseType: 'blob',
      });
      const url = window.URL.createObjectURL(new Blob([r.data], { type: 'application/pdf' }));
      const a = document.createElement('a');
      a.href = url;
      a.download = `Rapport_dossier_${id}.pdf`;
      document.body.appendChild(a); a.click(); a.remove();
      setTimeout(() => window.URL.revokeObjectURL(url), 1000);
      toast.success('Rapport téléchargé');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Erreur téléchargement');
    } finally { setDownloading(false); }
  };

  const sendReport = async () => {
    setSending(true);
    try {
      const body = { message: customMsg.trim() || undefined };
      if (sendMode === 'custom') body.email = customEmail.trim();
      const r = await api.post(`/cee/dossiers/${id}/rapport/send`, body);
      if (r.data.email_sent) {
        toast.success(`Rapport envoyé à ${r.data.sent_to}`);
      } else {
        toast.warning('Envoi email échoué — réessayez');
      }
      setShowSendModal(false);
      setCustomMsg(''); setCustomEmail('');
      onReload && onReload();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Erreur envoi rapport');
    } finally { setSending(false); }
  };

  const hasAmo = Boolean(doc?.amo_id && doc?.amo_email);
  const amoLabel = doc?.amo_nom
    ? `${doc.amo_nom} (${doc.amo_email || 'email manquant'})`
    : '—';

  return (
    <div className="space-y-4" data-testid="rapport-tab">
      {/* ================= Adresse complète bénéficiaire ================= */}
      <Card className="p-5 border-slate-200 space-y-2">
        <div className="flex items-center gap-2">
          <MapPin className="w-5 h-5 text-[#004AAD]" />
          <h2 className="font-bold text-slate-900">Adresse complète du bénéficiaire</h2>
        </div>
        <p className="text-xs text-slate-500">
          Précisez l'adresse postale complète (N° + rue + code postal + ville).
          Nécessaire pour le transfert à l'AMO.
        </p>
        <Textarea
          rows={2}
          value={form.adresse_complete}
          onChange={(e) => setForm((f) => ({ ...f, adresse_complete: e.target.value }))}
          placeholder="12 rue des Lilas · 75019 Paris · Bâtiment B, 2e étage"
          data-testid="rapport-adresse-complete"
        />
      </Card>

      {/* ================= Éligibilité ================= */}
      <Card className="p-5 border-slate-200 space-y-3">
        <div className="flex items-center gap-2">
          <ClipboardCheck className="w-5 h-5 text-[#004AAD]" />
          <h2 className="font-bold text-slate-900">Type d'éligibilité MaPrimeAdapt'</h2>
        </div>
        <p className="text-xs text-slate-500">Cochez le(s) critère(s) validé(s) pour ce bénéficiaire.</p>
        <div className="space-y-2 pt-1">
          {ELIGIBILITY_OPTIONS.map((opt) => (
            <label
              key={opt.key}
              className={`flex items-start gap-3 p-3 rounded-lg border cursor-pointer transition-colors ${form.eligibilite[opt.key] ? 'border-emerald-300 bg-emerald-50' : 'border-slate-200 hover:bg-slate-50'}`}
              data-testid={`rapport-elig-${opt.key}`}
            >
              <input
                type="checkbox"
                checked={!!form.eligibilite[opt.key]}
                onChange={() => toggleElig(opt.key)}
                className="mt-0.5 h-4 w-4 accent-emerald-600"
              />
              <span className="text-sm text-slate-800 leading-snug">{opt.label}</span>
            </label>
          ))}
        </div>
      </Card>

      {/* ================= Situation fiscale ================= */}
      <Card className="p-5 border-slate-200 space-y-3">
        <h2 className="font-bold text-slate-900">Situation fiscale & civile</h2>
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3">
          <div>
            <label className="text-xs font-medium text-slate-600 block mb-1">Âge</label>
            <Input
              type="number" min="0" max="130"
              value={form.age}
              onChange={(e) => setForm((f) => ({ ...f, age: e.target.value }))}
              placeholder="ex. 75"
              data-testid="rapport-input-age"
            />
          </div>
          <div>
            <label className="text-xs font-medium text-slate-600 block mb-1">
              Composition du ménage
              <span className="text-slate-400 font-normal"> (nb personnes)</span>
            </label>
            <Input
              type="number" min="1" step="1"
              value={form.menage_personnes}
              onChange={(e) => setForm((f) => ({ ...f, menage_personnes: e.target.value }))}
              placeholder="ex. 2"
              data-testid="rapport-input-personnes"
            />
          </div>
          <div>
            <label className="text-xs font-medium text-slate-600 block mb-1">RFR (€)</label>
            <Input
              type="number" min="0" step="100"
              value={form.revenu_fiscal_reference}
              onChange={(e) => setForm((f) => ({ ...f, revenu_fiscal_reference: e.target.value }))}
              placeholder="ex. 18 500"
              data-testid="rapport-input-rfr"
            />
          </div>
          <div>
            <label className="text-xs font-medium text-slate-600 block mb-1">
              Parts fiscales
            </label>
            <Input
              type="number" min="0" step="0.25"
              value={form.nombre_parts}
              onChange={(e) => setForm((f) => ({ ...f, nombre_parts: e.target.value }))}
              placeholder="ex. 1.5"
              data-testid="rapport-input-parts"
            />
          </div>
        </div>

        {/* Badge barème Anah 2026 — live-computed */}
        {bareme && (bareme.personnes || bareme.rfr) && (
          <AnahBaremeCard bareme={bareme} dossierCp={dossierCp} />
        )}

        <div className="grid sm:grid-cols-2 gap-3 pt-2">
          <YesNoField label="Compte Anah actif ?"
            value={form.compte_anah}
            onChange={(v) => setYN('compte_anah', v)}
            testid="rapport-compte-anah"
          />
          <YesNoField label="Identité numérique validée ?"
            value={form.identite_numerique}
            onChange={(v) => setYN('identite_numerique', v)}
            testid="rapport-identite-numerique"
          />
        </div>
      </Card>

      {/* ================= Documents ================= */}
      <Card className="p-5 border-slate-200 space-y-3">
        <h2 className="font-bold text-slate-900">Documents en possession du bénéficiaire</h2>
        <p className="text-xs text-slate-500">Cochez les documents déjà fournis par le bénéficiaire.</p>
        <div className="grid sm:grid-cols-2 gap-2">
          {DOCUMENT_OPTIONS.map((opt) => (
            <label
              key={opt.key}
              className={`flex items-center gap-3 p-3 rounded-lg border cursor-pointer transition-colors ${form.documents[opt.key] ? 'border-emerald-300 bg-emerald-50' : 'border-slate-200 hover:bg-slate-50'}`}
              data-testid={`rapport-doc-${opt.key}`}
            >
              <input
                type="checkbox"
                checked={!!form.documents[opt.key]}
                onChange={() => toggleDoc(opt.key)}
                className="h-4 w-4 accent-emerald-600"
              />
              <span className="text-sm text-slate-800">{opt.label}</span>
            </label>
          ))}
        </div>
      </Card>

      {/* ================= Notes ================= */}
      <Card className="p-5 border-slate-200 space-y-2">
        <h2 className="font-bold text-slate-900">Notes complémentaires</h2>
        <Textarea
          value={form.notes}
          onChange={(e) => setForm((f) => ({ ...f, notes: e.target.value }))}
          rows={3}
          placeholder="Précisions pour l'AMO (contexte, mobilité, besoins spécifiques…)"
          data-testid="rapport-notes"
        />
      </Card>

      <DocRequestPanel doc={doc} id={id} />

      {/* ================= Actions ================= */}
      <Card className="p-5 border-slate-200 bg-slate-50">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div className="text-xs text-slate-600 min-w-0">
            <div><b>AMO rattaché :</b> {amoLabel}</div>
            {doc?.rapport_last_sent_at && (
              <div className="mt-0.5 text-slate-500">
                Dernier envoi : {fmtDateTime(doc.rapport_last_sent_at)} → {doc.rapport_last_sent_to}
              </div>
            )}
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            <Button onClick={save} disabled={saving}
              className="bg-[#004AAD] hover:bg-[#003580]"
              data-testid="rapport-save-btn">
              <Save className="w-4 h-4 mr-2" />
              {saving ? 'Enregistrement…' : 'Enregistrer'}
            </Button>
            <Button variant="outline" onClick={downloadPdf} disabled={downloading}
              data-testid="rapport-download-btn">
              <FileDown className="w-4 h-4 mr-2" />
              {downloading ? 'Génération…' : 'Télécharger PDF'}
            </Button>
            <Button
              onClick={() => { setShowSendModal(true); setSendMode(hasAmo ? 'amo' : 'custom'); }}
              className="bg-emerald-600 hover:bg-emerald-700"
              data-testid="rapport-send-btn"
            >
              <Send className="w-4 h-4 mr-2" /> Transmettre à l'AMO
            </Button>
          </div>
        </div>
      </Card>

      {showSendModal && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4" onClick={() => setShowSendModal(false)}>
          <div className="bg-white rounded-xl shadow-xl max-w-lg w-full p-6 space-y-4" onClick={(e) => e.stopPropagation()}>
            <div>
              <h3 className="font-bold text-slate-900">Transmettre le rapport</h3>
              <p className="text-xs text-slate-500">Choisissez le destinataire du PDF.</p>
            </div>

            <div className="space-y-2">
              <label className={`flex items-start gap-3 p-3 rounded-lg border cursor-pointer ${sendMode === 'amo' ? 'border-[#004AAD] bg-blue-50' : 'border-slate-200'} ${!hasAmo ? 'opacity-50 cursor-not-allowed' : ''}`}>
                <input type="radio" checked={sendMode === 'amo'} disabled={!hasAmo}
                  onChange={() => setSendMode('amo')} data-testid="rapport-mode-amo" />
                <div className="flex-1">
                  <div className="font-medium text-slate-900 text-sm">Envoyer à l'AMO assigné</div>
                  <div className="text-xs text-slate-500 mt-0.5">{hasAmo ? amoLabel : 'Aucun AMO rattaché ou email manquant'}</div>
                </div>
              </label>
              <label className={`flex items-start gap-3 p-3 rounded-lg border cursor-pointer ${sendMode === 'custom' ? 'border-[#004AAD] bg-blue-50' : 'border-slate-200'}`}>
                <input type="radio" checked={sendMode === 'custom'}
                  onChange={() => setSendMode('custom')} data-testid="rapport-mode-custom" />
                <div className="flex-1">
                  <div className="font-medium text-slate-900 text-sm">Choisir un autre destinataire</div>
                  {sendMode === 'custom' && (
                    <Input
                      className="mt-2"
                      type="email"
                      value={customEmail}
                      onChange={(e) => setCustomEmail(e.target.value)}
                      placeholder="email@exemple.fr"
                      data-testid="rapport-custom-email"
                    />
                  )}
                </div>
              </label>
            </div>

            <div>
              <label className="text-xs font-medium text-slate-600 block mb-1">Message personnalisé (optionnel)</label>
              <Textarea rows={3} value={customMsg} onChange={(e) => setCustomMsg(e.target.value)}
                placeholder="Ajoutez un message qui sera inséré dans l'email…"
                data-testid="rapport-send-message" />
            </div>

            <div className="flex justify-end gap-2 pt-2">
              <Button variant="outline" onClick={() => setShowSendModal(false)}>Annuler</Button>
              <Button
                onClick={sendReport}
                disabled={sending || (sendMode === 'custom' && !customEmail.trim()) || (sendMode === 'amo' && !hasAmo)}
                className="bg-emerald-600 hover:bg-emerald-700"
                data-testid="rapport-send-confirm"
              >
                <Send className="w-4 h-4 mr-2" />
                {sending ? 'Envoi…' : 'Envoyer le rapport'}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function AnahBaremeCard({ bareme, dossierCp }) {
  if (!bareme) return null;
  const fmtE = (n) => n == null ? '—'
    : new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'EUR', maximumFractionDigits: 0 }).format(n);

  const PALETTES = {
    tres_modeste: {
      box: 'bg-blue-50 border-blue-200',
      pill: 'bg-blue-600 text-white',
      text: 'text-blue-900',
      sub: 'text-blue-700',
      label: 'TRÈS MODESTE (bleu)',
      elig: 'Éligible MaPrimeAdapt\' — jusqu\'à 70 % de prime.',
    },
    modeste: {
      box: 'bg-amber-50 border-amber-200',
      pill: 'bg-amber-500 text-white',
      text: 'text-amber-900',
      sub: 'text-amber-700',
      label: 'MODESTE (jaune)',
      elig: 'Éligible MaPrimeAdapt\' — jusqu\'à 50 % de prime.',
    },
    intermediaire: {
      box: 'bg-red-50 border-red-200',
      pill: 'bg-red-600 text-white',
      text: 'text-red-900',
      sub: 'text-red-700',
      label: 'INTERMÉDIAIRE',
      elig: 'Non éligible aux aides MaPrimeAdapt\' sous conditions de ressources.',
    },
  };
  const incomplete = !bareme.complete || !bareme.category;
  const pal = PALETTES[bareme.category] || {
    box: 'bg-slate-50 border-slate-200', pill: 'bg-slate-400 text-white',
    text: 'text-slate-800', sub: 'text-slate-500',
    label: 'À COMPLÉTER',
    elig: 'Renseignez composition du ménage et RFR pour déterminer la catégorie Anah.',
  };

  return (
    <div className={`mt-3 p-4 rounded-lg border-2 ${pal.box}`} data-testid="anah-bareme-card">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-2 flex-wrap">
          <span className={`px-3 py-1 rounded-full text-xs font-bold uppercase tracking-wide ${pal.pill}`}>
            Anah 2026 · {pal.label}
          </span>
          <span className={`text-xs font-medium ${pal.sub}`}>
            Zone : <b>{bareme.zone_label}</b>
            {dossierCp && <> · CP {dossierCp}</>}
            {bareme.personnes && <> · {bareme.personnes} pers.</>}
          </span>
        </div>
      </div>
      <p className={`text-sm font-semibold mt-2 ${pal.text}`}>
        {pal.elig}
      </p>
      {bareme.plafond_tres_modeste && (
        <div className={`grid grid-cols-2 gap-3 mt-3 text-xs ${pal.sub}`}>
          <div className="flex items-center gap-2">
            <span className="w-2 h-2 rounded-full bg-blue-600" />
            Plafond <b>Très modeste</b> : {fmtE(bareme.plafond_tres_modeste)}
          </div>
          <div className="flex items-center gap-2">
            <span className="w-2 h-2 rounded-full bg-amber-500" />
            Plafond <b>Modeste</b> : {fmtE(bareme.plafond_modeste)}
          </div>
        </div>
      )}
      {incomplete && (
        <p className="text-[11px] text-slate-500 italic mt-2">
          Barème 2026 appliqué dès que composition du ménage + RFR renseignés.
        </p>
      )}
    </div>
  );
}

function YesNoField({ label, value, onChange, testid }) {
  return (
    <div data-testid={testid}>
      <label className="text-xs font-medium text-slate-600 block mb-1">{label}</label>
      <div className="flex gap-2">
        {[
          { v: true, lbl: 'Oui' }, { v: false, lbl: 'Non' }, { v: null, lbl: '—' },
        ].map((o) => (
          <button
            key={String(o.v)}
            type="button"
            onClick={() => onChange(o.v)}
            className={`flex-1 px-3 py-1.5 rounded-md border text-sm transition-colors ${value === o.v ? 'bg-[#004AAD] text-white border-[#004AAD]' : 'border-slate-200 text-slate-700 hover:bg-slate-50'}`}
            data-testid={`${testid}-${o.v === null ? 'na' : o.v ? 'yes' : 'no'}`}
          >
            {o.lbl}
          </button>
        ))}
      </div>
    </div>
  );
}


function PrequalifTab({ doc, id, onReload }) {
  const [busy, setBusy] = useState(false);
  const [sentInfo, setSentInfo] = useState(null);
  const [liveMode, setLiveMode] = useState(null);  // null = chargement
  const qStatus = doc.questionnaire_status || 'not_sent';
  const evalResult = doc.questionnaire_evaluation;

  // V3.1m-fix — Lecture du flag live mode pour adapter l'UI.
  // IMPORTANT : si l'appel échoue (session expirée, timeout), on garde
  // le défaut **LIVE** (identique au défaut backend `enabled=True`).
  // Afficher un bandeau "mode TEST" sur un échec réseau serait trompeur
  // et bloquerait l'envoi réel du formulaire.
  useEffect(() => {
    (async () => {
      try {
        const r = await api.get('/prospect-quiz/live-mode/status');
        // enabled peut être true/false/undefined → on considère undefined=true
        setLiveMode(r.data?.enabled !== false);
      } catch { setLiveMode(true); }
    })();
  }, []);

  // Destinataire réel calculé (demandeur si LIVE, sinon TEST_EMAIL)
  const demandeurEmail = (doc.demandeur || {}).email || null;
  const beneficiaireEmail = (doc.beneficiaire || {}).email || null;
  const effectiveRealRecipient = demandeurEmail
    || (doc.demandeur_est_beneficiaire ? beneficiaireEmail : null);

  const sendQuiz = async () => {
    if (busy) return;
    setBusy(true);
    try {
      const r = await api.post(`/prospect-quiz/send/${id}`);
      setSentInfo(r.data);
      if (r.data.email_sent_to) {
        const isLive = r.data.mode === 'real'
          || (!r.data.mode && liveMode === true);
        toast.success(
          isLive
            ? `Email envoyé au prospect : ${r.data.email_sent_to}`
            : `Email de test envoyé à ${r.data.email_sent_to}`
        );
      } else {
        toast.warning('Lien créé mais email non envoyé. Copiez le lien manuellement.');
      }
      onReload && onReload();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Erreur envoi quiz');
    } finally { setBusy(false); }
  };

  const copyLink = () => {
    if (!sentInfo?.quiz_url) return;
    navigator.clipboard.writeText(sentInfo.quiz_url);
    toast.success('Lien quiz copié');
  };

  const VERDICT_COLORS = {
    emerald: 'bg-emerald-50 border-emerald-200 text-emerald-900',
    amber:   'bg-amber-50 border-amber-200 text-amber-900',
    red:     'bg-red-50 border-red-200 text-red-900',
    slate:   'bg-slate-50 border-slate-200 text-slate-900',
  };
  const VERDICT_ICONS = {
    emerald: CheckCircle2, amber: AlertTriangle, red: XCircle, slate: Info,
  };

  const Q_LABELS = {
    q1_beneficiaire: 'Situation du bénéficiaire des travaux',
    q2_occupation: 'Statut d\'occupation du logement',
    q2a_accord_bailleur: 'Accord du bailleur (si locataire)',
    q3_residence_principale: 'Résidence principale ?',
    q4_code_postal: 'Code postal',
    q4_cp_inconnu: 'Code postal inconnu ?',
    q5_nb_personnes: 'Nombre de personnes du foyer',
    q5_nb_personnes_inconnu: 'Nombre de personnes inconnu ?',
    q6_rfr_eur: 'Revenu fiscal de référence (€)',
    q6_rfr_inconnu: 'RFR inconnu ?',
    projet_confirme: 'Projet « douche adaptée » confirmé',
  };
  const V_LABELS = {
    '70_plus': '70 ans ou plus',
    '60_69_gir': '60 à 69 ans avec un GIR reconnu',
    handicap: 'Situation de handicap ≥ 50% / PCH',
    autre: 'Autre situation',
    inconnu: 'Je ne sais pas',
    proprio_occupant: 'Propriétaire occupant',
    proprio_bailleur: 'Propriétaire bailleur',
    locataire_prive: 'Locataire du parc privé',
    locataire_social: 'Locataire du parc social',
    obtenu: 'Accord obtenu',
    a_demander: 'À demander',
    refuse: 'Refusé',
    oui: 'Oui', non: 'Non',
    true: 'Oui', false: 'Non', 'True': 'Oui', 'False': 'Non',
  };
  const prettyValue = (k, v) => {
    if (v === null || v === undefined || v === '') return '—';
    if (typeof v === 'boolean') return v ? 'Oui' : 'Non';
    const str = String(v);
    return V_LABELS[str] || str;
  };

  return (
    <Card className="p-4 sm:p-6 border-slate-200 space-y-5" data-testid="prequalif-card">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <h2 className="font-bold text-slate-900">Questionnaire de préqualification</h2>
        <Badge className={`border-0 ${qStatus === 'responded' ? 'bg-emerald-100 text-emerald-700' : qStatus === 'sent' ? 'bg-blue-100 text-blue-700' : 'bg-slate-100 text-slate-600'}`}>
          {qStatus === 'not_sent' ? 'Non envoyé' : qStatus === 'sent' ? 'Envoyé' : 'Répondu'}
        </Badge>
      </div>

      {/* État "Non envoyé" */}
      {qStatus === 'not_sent' && (
        <div className="space-y-3">
          <div className="p-5 bg-slate-50 rounded-lg text-center space-y-2">
            <ClipboardList className="w-10 h-10 mx-auto text-slate-300" />
            <p className="text-sm text-slate-700 font-medium">Questionnaire non envoyé</p>
            {liveMode === true && effectiveRealRecipient && (
              <p className="text-xs text-slate-500 max-w-lg mx-auto"
                 data-testid="prequalif-live-note">
                Mode <b className="text-emerald-700">LIVE</b> actif : un email avec lien
                personnel (valide 7 jours) sera envoyé au prospect
                <code className="mx-1 px-1 bg-white rounded text-[11px]">
                  {effectiveRealRecipient}
                </code>.
              </p>
            )}
            {liveMode === true && !effectiveRealRecipient && (
              <p className="text-xs text-amber-700 max-w-lg mx-auto"
                 data-testid="prequalif-no-email-note">
                Mode <b>LIVE</b> actif mais aucun email demandeur valide trouvé.
                L'envoi sera marqué <code className="bg-white px-1 rounded">phone_needed</code>.
              </p>
            )}
            {liveMode === false && (
              <p className="text-xs text-slate-500 max-w-lg mx-auto"
                 data-testid="prequalif-test-note">
                Mode <b className="text-slate-700">TEST</b> actif : un email contenant un lien
                personnel (valide 7 jours) sera envoyé au conseiller
                <code className="mx-1 px-1 bg-white rounded text-[11px]">aberbry@gmail.com</code>.
                Aucun prospect réel ne reçoit d'email.
              </p>
            )}
            {liveMode === null && (
              <p className="text-xs text-slate-400 italic">Chargement du mode d'envoi…</p>
            )}
          </div>
          <Button onClick={sendQuiz} disabled={busy || liveMode === null}
            className="w-full sm:w-auto bg-[#1C49A6] hover:bg-[#173B85]"
            data-testid="send-quiz-btn">
            <Send className="w-4 h-4 mr-2" />
            {busy
              ? 'Envoi…'
              : liveMode
                ? `Envoyer le questionnaire au prospect${effectiveRealRecipient ? ` (${effectiveRealRecipient})` : ''}`
                : 'Envoyer le questionnaire (test → aberbry@gmail.com)'}
          </Button>
        </div>
      )}

      {/* État "Envoyé" */}
      {qStatus === 'sent' && (
        <div className="space-y-3">
          <div className="text-sm text-slate-700">
            Lien envoyé le <b>{fmtDateTime(doc.questionnaire_sent_at)}</b>.
          </div>

          {/* Statut d'ouverture (tracking GET /{token}) */}
          {doc.questionnaire_opened_at ? (
            <div className="flex items-center gap-2 text-xs text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-md px-3 py-2"
                 data-testid="prequalif-opened-indicator">
              <CheckCircle2 className="w-3.5 h-3.5" />
              Lien ouvert par le prospect le <b>{fmtDateTime(doc.questionnaire_opened_at)}</b> — en attente de réponse.
            </div>
          ) : (
            <div className="flex items-center gap-2 text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-md px-3 py-2"
                 data-testid="prequalif-not-opened-indicator">
              <AlertTriangle className="w-3.5 h-3.5" />
              Le prospect n'a pas encore ouvert le lien. Pense à relancer après 2-3 jours.
            </div>
          )}

          {sentInfo?.quiz_url && (
            <div className="flex flex-wrap gap-2">
              <Button variant="outline" size="sm" onClick={copyLink}
                data-testid="copy-quiz-link">
                <Copy className="w-3.5 h-3.5 mr-1.5" /> Copier le lien
              </Button>
              <a href={sentInfo.quiz_url} target="_blank" rel="noopener noreferrer"
                className="inline-flex items-center gap-1.5 text-xs text-[#1C49A6] hover:underline">
                <ExternalLink className="w-3.5 h-3.5" /> Ouvrir le quiz (aperçu)
              </a>
            </div>
          )}

          <Button
            onClick={sendQuiz}
            disabled={busy || liveMode === null}
            className="w-full sm:w-auto bg-[#1C49A6] hover:bg-[#173B85] text-white"
            data-testid="renew-quiz-btn">
            <RefreshCw className="w-4 h-4 mr-2" />
            {busy ? 'Envoi…'
                   : liveMode
                     ? `Relancer le prospect${effectiveRealRecipient ? ` (${effectiveRealRecipient})` : ''}`
                     : 'Relancer (test → aberbry@gmail.com)'}
          </Button>
        </div>
      )}

      {/* État "Répondu" */}
      {qStatus === 'responded' && (
        <div className="space-y-4">
          {evalResult && (() => {
            const Icon = VERDICT_ICONS[evalResult.verdict_color] || Info;
            return (
              <div className={`border rounded-lg p-4 ${VERDICT_COLORS[evalResult.verdict_color] || VERDICT_COLORS.slate}`}
                   data-testid="verdict-card">
                <div className="flex items-start gap-3">
                  <Icon className="w-6 h-6 shrink-0 mt-0.5" />
                  <div className="flex-1 min-w-0">
                    <div className="text-[10px] uppercase font-semibold opacity-70">
                      Résultat provisoire — un conseiller doit confirmer
                    </div>
                    <div className="font-bold text-base mt-0.5">
                      {evalResult.verdict_label}
                    </div>
                    {evalResult.plafond_applicable_eur && (
                      <div className="text-xs mt-1 opacity-80">
                        Plafond modestes applicable : {new Intl.NumberFormat('fr-FR').format(evalResult.plafond_applicable_eur)} € ·
                        Zone : {evalResult.zone === 'idf' ? 'Île-de-France' : evalResult.zone === 'hors_idf' ? 'Hors Île-de-France' : 'Non déterminée'}
                      </div>
                    )}
                  </div>
                </div>

                {(evalResult.reasons_block?.length > 0) && (
                  <div className="mt-3 pt-3 border-t border-current/20">
                    <div className="text-[10px] uppercase font-semibold opacity-70 mb-1.5">
                      Blocages confirmés
                    </div>
                    <ul className="space-y-1.5 text-xs">
                      {evalResult.reasons_block.map((r, i) => (
                        <li key={i} className="flex items-start gap-1.5">
                          <XCircle className="w-3.5 h-3.5 shrink-0 mt-0.5" />
                          <div>
                            <div>{r.message}</div>
                            <div className="opacity-60 text-[10px]">Source : {r.source}</div>
                          </div>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}

                {(evalResult.reasons_verify?.length > 0) && (
                  <div className="mt-3 pt-3 border-t border-current/20">
                    <div className="text-[10px] uppercase font-semibold opacity-70 mb-1.5">
                      Points à vérifier
                    </div>
                    <ul className="space-y-1.5 text-xs">
                      {evalResult.reasons_verify.map((r, i) => (
                        <li key={i} className="flex items-start gap-1.5">
                          <AlertTriangle className="w-3.5 h-3.5 shrink-0 mt-0.5" />
                          <div>
                            <div>{r.message}</div>
                            <div className="opacity-60 text-[10px]">Source : {r.source}</div>
                          </div>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
            );
          })()}

          <div className="grid sm:grid-cols-2 gap-2 text-xs text-slate-600">
            <div>Envoyé : <b>{fmtDateTime(doc.questionnaire_sent_at)}</b></div>
            <div>Répondu : <b>{fmtDateTime(doc.questionnaire_responded_at)}</b></div>
          </div>

          {doc.questionnaire_answers && (
            <div>
              <h3 className="text-sm font-semibold text-slate-900 mb-2">Réponses détaillées</h3>
              <div className="space-y-2">
                {Object.entries(doc.questionnaire_answers)
                  .filter(([k]) => !k.endsWith('_inconnu') || doc.questionnaire_answers[k])
                  .map(([k, v]) => (
                    <div key={k} className="pb-2 border-b border-slate-100">
                      <div className="text-xs text-slate-500">{Q_LABELS[k] || k.replace(/_/g, ' ')}</div>
                      <div className="text-sm text-slate-900 font-medium">
                        {prettyValue(k, v)}
                      </div>
                    </div>
                  ))}
              </div>
            </div>
          )}
        </div>
      )}
    </Card>
  );
}

